import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  CircularProgress,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  HowToReg as HowToRegIcon,
} from '@mui/icons-material';

import './interaction/tokens.css';
import { auth, db } from './firebase-config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CompanyManager from './components/CompanyManager';
import RoleManager from './components/RoleManager';
import UserManager from './components/UserManager';
import SignupRequestManager from './components/SignupRequestManager';
import AppLayoutDesktop from './layouts/AppLayoutDesktop';
import AppLayoutMobile from './layouts/AppLayoutMobile';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#613C92',
      light: '#8B6BB5',
      dark: '#4F3175',
    },
    secondary: {
      main: '#F2EAFF',
      light: '#F9F7FC',
      dark: '#E8DDFF',
    },
    background: {
      // The page is tinted; the components sitting on it are white.
      default: '#f2e9ff',
      paper: '#ffffff',
    },
    success: { main: '#4CAF50' },
    warning: { main: '#FF9800' },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 600 },
    h2: { fontSize: '2rem', fontWeight: 600 },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 8 } },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 16 } },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 8 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
  },
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingSignupCount, setPendingSignupCount] = useState(0);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'signupRequests'), where('status', '==', 'pending'));
    const unsub = onSnapshot(q, (snap) => setPendingSignupCount(snap.size), () => {});
    return () => unsub();
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        try {
          const idTokenResult = await authUser.getIdTokenResult();
          const { claims } = idTokenResult;
          const hasAdminAccess = claims.roles && claims.roles.includes('system_admin');
          if (hasAdminAccess) {
            setUser({
              uid: authUser.uid,
              email: authUser.email,
              displayName: authUser.displayName,
              photoURL: authUser.photoURL,
              claims,
            });
            setError(null);
          } else {
            setError('Access denied. System administrator permissions required.');
            setUser(null);
            await signOut(auth);
          }
        } catch (err) {
          console.error('Error getting user claims:', err);
          setError('Error verifying permissions');
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', requiredRole: null },
    { text: 'Companies', icon: <BusinessIcon />, path: '/companies', requiredRole: 'system_admin' },
    {
      text: 'Signup Requests',
      icon: <Badge badgeContent={pendingSignupCount} color="error" max={99}><HowToRegIcon /></Badge>,
      path: '/signup-requests',
      requiredRole: 'system_admin',
    },
    { text: 'Users', icon: <PeopleIcon />, path: '/users', requiredRole: 'admin' },
    { text: 'Roles', icon: <SecurityIcon />, path: '/roles', requiredRole: 'admin' },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login error={error} />
      </ThemeProvider>
    );
  }

  const Layout = isMobile ? AppLayoutMobile : AppLayoutDesktop;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Layout user={user} menuItems={menuItems} onLogout={handleLogout}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard user={user} />} />
          <Route path="/signup-requests" element={<SignupRequestManager />} />
          <Route path="/users" element={<UserManager user={user} />} />
          <Route path="/companies" element={<CompanyManager user={user} />} />
          <Route path="/roles" element={<RoleManager user={user} />} />
        </Routes>
      </Layout>
    </ThemeProvider>
  );
}

export default App;
